import React, { useEffect } from "react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  CloseButton,
} from "@headlessui/react";
import { ScheduleTimeType } from "../scheduledTimeDropdown/scheduledTimeDropdown";
import { DateTime } from "luxon";
import { getSchedulableDateOptions } from "@/utils/scheduleOrderUtils";
import { useTranslations } from "next-intl";
import { DayPart } from "@/dto/dayPartMenu";

export type ScheduleDateType = {
  displayValue: string;
  dateObject: Date;
};

export const ScheduledDateDropdown = ({
  scheduleDate,
  setScheduleDate,
  scheduleDateRange,
  setScheduleTime,
  businessHours,
  timezone,
  leadTime,
  selectedDayPart = null,
}: {
  scheduleDate: ScheduleDateType;
  // eslint-disable-next-line no-unused-vars
  setScheduleDate: (newDate: ScheduleDateType) => void;
  scheduleDateRange: number;
  // eslint-disable-next-line no-unused-vars
  setScheduleTime: (newTime: ScheduleTimeType) => void;
  businessHours: any;
  timezone: string;
  leadTime?: number;
  selectedDayPart?: DayPart | null;
}) => {
  const t = useTranslations("ScheduledDateDropdown");
  const dateOptions: ScheduleDateType[] = getSchedulableDateOptions(
    scheduleDateRange,
    businessHours,
    timezone,
    leadTime,
    selectedDayPart
  );

  useEffect(() => {
    if (
      !dateOptions.some((option) => {
        const scheduleDateObj = DateTime.fromJSDate(
          typeof scheduleDate.dateObject === "string"
            ? new Date(scheduleDate.dateObject)
            : scheduleDate.dateObject,
          {
            zone: timezone,
          }
        );
        const optionDateObj = DateTime.fromJSDate(option.dateObject, {
          zone: timezone,
        });

        return (
          option.displayValue === scheduleDate.displayValue &&
          optionDateObj.hasSame(scheduleDateObj, "day")
        );
      })
    ) {
      setScheduleDate(dateOptions[0]);
    }
  }, [dateOptions, scheduleDate, setScheduleDate, timezone]);

  return (
    <Popover className="relative">
      <PopoverButton
        className={[
          "flex",
          "items-center",
          "gap-x-2",
          "w-full",
          "leading-6",
          "text-gray-900",
          "hover:bg-gray-50",
        ].join(" ")}
      >
        <input
          type="text"
          id="scheduled_date_input"
          className="block px-2.5 h-[58px] py-2.5 w-full text-gray-900 bg-transparent rounded-[4px] border border-black hover:border-black focus:outline-none focus:ring-1 focus:ring-blue peer cursor-pointer"
          value={scheduleDate?.displayValue}
          readOnly
        />
        <label
          htmlFor="scheduled_date_input"
          className="absolute text-md text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-5 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-5 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
        >
          {t("date")}
        </label>
      </PopoverButton>
      <PopoverPanel
        transition
        className="absolute top-full z-20 w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
        style={{ maxHeight: "300px", overflowY: "auto" }}
      >
        <div className="p-3">
          {dateOptions.map((option, index) => (
            <CloseButton
              key={index}
              className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 leading-6 hover:bg-gray-50 flex-auto"
              onClick={() => {
                setScheduleDate(option);
                setScheduleTime({
                  displayValue: "",
                  timeObject: new Date(),
                });
              }}
            >
              <p className="text-[15px]">{option.displayValue}</p>
            </CloseButton>
          ))}
        </div>
      </PopoverPanel>
    </Popover>
  );
};
